import React, { useEffect, useState } from 'react';
import axios from 'axios';

const criteria = {
  cumpleanos: 'Fecha de nacimiento: solo quienes cumplen años en el día local de ejecución.',
  navidad: 'Solo el 25 de diciembre.',
  ano_nuevo: 'Solo el 1 de enero.',
  accion_de_gracias: 'Solo el cuarto jueves de noviembre.',
  recordatorio_cita: 'Cita o reunión registrada en el CRM para el día siguiente (salvo anticipación configurada).',
  recordatorio_audiencia: 'Audiencia registrada en EOIR o en el calendario del cliente para el día siguiente (salvo anticipación configurada).',
  recordatorio_pago: 'Día de cobro mensual registrado y honorarios pendientes según los pagos del CRM.',
};

export default function AutomationMessageEditor({ rule, clients, api, onSaved, onNotice }) {
  const [draft, setDraft] = useState(rule.plantilla || '');
  const [channel, setChannel] = useState(rule.canal);
  const [recipient, setRecipient] = useState(rule.cliente_id || '');
  const [sampleId, setSampleId] = useState(rule.cliente_id || '');
  const [instructions, setInstructions] = useState('');
  const [image, setImage] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [busy, setBusy] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [audience, setAudience] = useState(null);
  const dirty = draft !== (rule.plantilla || '') || channel !== rule.canal || String(recipient) !== String(rule.cliente_id || '');

  useEffect(() => {
    let active = true;
    setAudience(null);
    axios.get(`${api}/automations/${rule.id}/audience`).then(r => { if (active) setAudience(r.data); }).catch(() => { if (active) setAudience({ error: true }); });
    return () => { active = false; };
  }, [api, rule.id, rule.cliente_id, rule.updated_at]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setImage(''); setPreviewText(''); setPreviewError('');
    setRendering(Boolean(draft.trim()));
    const timer = setTimeout(async () => {
      if (!draft.trim()) return;
      try {
        const r = await axios.post(`${api}/automations/email-design-preview`, { tipo: rule.tipo, idioma: rule.idioma, contenido: draft, cliente_id: sampleId ? Number(sampleId) : null, automation_id: rule.id }, { signal: controller.signal });
        if (active) { setImage(r.data.image); setPreviewText(r.data.contenido); }
      } catch (e) { if (active && !controller.signal.aborted) setPreviewError('No se pudo actualizar la imagen. No se ha enviado ningún mensaje.'); }
      finally { if (active) setRendering(false); }
    }, 450);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [api, draft, sampleId, rule.id, rule.tipo, rule.idioma]);

  async function generate() {
    setBusy(true);
    try {
      const r = await axios.post(`${api}/automations/generate`, { tipo: rule.tipo, idioma: rule.idioma, plantilla: draft, instrucciones: instructions, usar_ia: true, como_plantilla: true });
      setDraft(r.data.content);
      onNotice('Texto generado. Puede editarlo; la imagen se actualiza automáticamente. Guárdelo para aprobarlo.');
    } catch (e) { onNotice(e.response?.data?.detail || 'No fue posible generar el texto.'); }
    finally { setBusy(false); }
  }
  async function save() {
    setBusy(true);
    try {
      const r = await axios.put(`${api}/automations/${rule.id}`, { plantilla: draft.trim(), usar_ia: false, canal: channel, cliente_id: recipient ? Number(recipient) : null });
      setDraft(r.data.plantilla);
      onSaved(r.data);
      onNotice('Texto final guardado. Se usarán sus palabras en la imagen, sustituyendo únicamente variables del CRM. No se cambió el estado activo/pausado.');
    } catch (e) { onNotice(e.response?.data?.detail || 'No fue posible guardar.'); }
    finally { setBusy(false); }
  }

  return <aside className="ai-preview ai-message-editor">
    <h2>{rule.nombre}</h2>
    <section><h3>1. Destinatarios y condición</h3>
      <label>Destinatarios de la regla<select value={recipient} onChange={e => setRecipient(e.target.value)}><option value="">Todos los clientes activos que cumplan la condición</option>{clients.map(c => <option key={c.id} value={c.id}>{c.nombre} {c.apellido}</option>)}</select></label>
      <p>{criteria[rule.tipo] || 'Clientes activos del alcance seleccionado, según la periodicidad de la regla.'} Seleccionar un cliente no elimina esta condición.</p>
      <label>Canal<select value={channel} onChange={e => setChannel(e.target.value)}><option value="email">Correo electrónico con imagen</option><option value="whatsapp">WhatsApp — revisar disponibilidad</option></select></label>
      {channel === 'whatsapp' && <p role="note" className="ai-channel-warning">Esta imagen está integrada al envío por correo. El motor automático de WhatsApp no está habilitado y su ruta actual envía texto, no esta tarjeta. La vista previa no confirma envío por WhatsApp. Use correo para enviar la imagen; no se cambia el canal sin guardar.</p>}
      <details><summary>Quién cumple hoy la regla guardada</summary>{audience?.error ? <p>No fue posible consultar destinatarios.</p> : audience ? <><p>{audience.fecha_evaluacion}: {audience.total} clientes. {audience.nota}</p><ul>{audience.clientes.map(c => <li key={c.id}>{c.nombre}</li>)}</ul></> : <p>Consultando datos del CRM…</p>}</details>
    </section>
    <section><h3>2. Texto final dentro de la imagen</h3>
      {rule.usar_ia && <p>Esta regla todavía genera texto al ejecutar. Al guardar aquí se fijará el texto aprobado, sin nuevas reescrituras.</p>}
      <label>Instrucciones para generar con IA<textarea rows={2} value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="Ej.: cálido, cercano, dos párrafos; sin mencionar el proceso legal en cumpleaños." /></label>
      <button type="button" disabled={busy} onClick={generate}>{busy ? 'Procesando…' : 'Generar / mejorar texto con IA'}</button>
      <label>Mensaje editable<textarea rows={8} maxLength={1600} value={draft} onChange={e => setDraft(e.target.value)} /></label>
      <p className="ai-variable-help">Use {'{{nombre}}'}, {'{{tipo_caso}}'} y {'{{fecha_relevante}}'}. Se sustituyen por cliente; no escriba el nombre de una persona en una regla de grupo. {draft.length}/1600</p>
      <button type="button" className="ai-primary" disabled={busy || !draft.trim()} onClick={save}>Guardar texto final y configuración</button>
      <p aria-live="polite">{dirty ? 'Hay cambios sin guardar. La imagen muestra su borrador, no modifica los envíos todavía.' : rule.usar_ia ? 'Pendiente de fijar como texto final.' : 'Texto aprobado guardado. Solo se personalizan las variables.'}</p>
    </section>
    <section><h3>3. Así quedará la imagen</h3>
      <label>Cliente de ejemplo — no cambia los destinatarios<select value={sampleId} onChange={e => setSampleId(e.target.value)}><option value="">Ver variables sin datos personales</option>{clients.map(c => <option key={c.id} value={c.id}>{c.nombre} {c.apellido}</option>)}</select></label>
      <p>El diseño se elige por el tipo de regla. La imagen se vuelve a construir con el texto completo para cada destinatario, al enviar.</p>
      {rendering && <p role="status">Actualizando imagen…</p>}{previewError && <p role="alert">{previewError}</p>}
      {image && <img className="ai-final-card" src={image} alt="Vista previa del texto editable incluido en la imagen de esta regla" />}
      {previewText && <details><summary>Texto personalizado de esta vista previa</summary><p style={{ whiteSpace: 'pre-wrap' }}>{previewText}</p></details>}
    </section>
  </aside>;
}
